/**
 * The client's view of a live game of klaverjassen.
 *
 * Third sibling of `store/live.ts` and `store/hearts.ts`, for the same reason
 * as the second: different state, different messages, same socket.
 */

import { create } from 'zustand';
import type { Card } from '@shared/types/game';
import type { KlaverjasLiveState } from '@shared/types/klaverjasLive';
import type { ServerMessage } from '@shared/types/websocket';
import { SUIT_LABELS } from '@/lib/labels';
import type { ConnectionStatus } from '@/live/socket';

/** Narrow the shared state union — only klaverjas has `dealNo`. */
function isKlaverjasState(state: unknown): state is KlaverjasLiveState {
  return typeof state === 'object' && state !== null && 'dealNo' in state;
}

export interface KlaverjasNotice {
  id: number;
  text: string;
}

export interface KlaverjasLiveData {
  state: KlaverjasLiveState | null;
  seat: number | null;
  code: string | null;
  status: ConnectionStatus;
  error: string | null;
  /** The code behind `error`, so a stale one can be retired. */
  errorCode: string | null;
  notices: KlaverjasNotice[];
}

interface KlaverjasLiveActions {
  handle: (message: ServerMessage) => void;
  setStatus: (status: ConnectionStatus) => void;
  dismissError: () => void;
  reset: () => void;
}

const EMPTY: KlaverjasLiveData = {
  state: null,
  seat: null,
  code: null,
  status: 'connecting',
  error: null,
  errorCode: null,
  notices: [],
};

let noticeId = 0;

/**
 * Errors that stop being true on their own.
 *
 * Only this one so far: everything else — an illegal card, a bad bid — is about
 * what you did and stays until you acknowledge it.
 */
function staleAfterTurnChange(code: string | null, isMyTurn: boolean): boolean {
  return code === 'NOT_YOUR_TURN' && isMyTurn;
}

function reduce(prev: KlaverjasLiveData, message: ServerMessage): Partial<KlaverjasLiveData> {
  const notice = (text: string): Partial<KlaverjasLiveData> => ({
    notices: [...prev.notices, { id: ++noticeId, text }].slice(-4),
  });
  const nameOf = (seat: number) => prev.state?.players[seat]?.name ?? `Speler ${seat + 1}`;

  switch (message.type) {
    case 'joined':
      if (!isKlaverjasState(message.state)) return {};
      return {
        seat: message.seat,
        state: message.state,
        code: message.state.code,
        error: null,
        errorCode: null,
      };

    case 'state-update': {
      if (!isKlaverjasState(message.state)) return {};

      // Retire an error the new snapshot has made untrue.
      const cleared = staleAfterTurnChange(prev.errorCode, message.state.turnSeat === prev.seat)
        ? { error: null, errorCode: null }
        : {};

      return { state: message.state, code: message.state.code, ...cleared };
    }

    case 'error':
      /*
       * Remember the code, not just the sentence.
       *
       * "Je bent niet aan de beurt" stays on screen until something replaces
       * it, so an impatient tap during someone else's turn leaves that banner
       * sitting there — still visible when your turn arrives and the panel
       * below it says the opposite. Keeping the code lets the next snapshot
       * retire it the moment it stops being true.
       */
      return { error: message.message, errorCode: message.code };

    case 'player-joined':
      return notice(`${message.name} is er`);

    case 'player-left':
      return notice(`${nameOf(message.seat)} is de verbinding kwijt`);

    case 'player-reconnected':
      return notice(`${nameOf(message.seat)} is er weer`);

    case 'deal-dealt':
      return notice(`Gift ${message.dealNo} — ${nameOf(message.chooserSeat)} kiest troef`);

    case 'trump-passed':
      return notice(`${nameOf(message.seat)} past`);

    case 'trump-chosen':
      return notice(`${nameOf(message.seat)} speelt op ${SUIT_LABELS[message.suit]}`);

    case 'trick-won':
      return notice(`${nameOf(message.seat)} pakt de slag`);

    case 'roem-claimed':
      return notice(`${nameOf(message.seat)} klopt ${message.points} roem`);

    case 'deal-scored': {
      const parts = [`Gift ${message.dealNo}: ${message.points[0]} — ${message.points[1]}`];
      if (message.nat) parts.push('nat!');
      if (message.pitTeam !== null) parts.push('pit!');
      return notice(parts.join(' '));
    }

    case 'game-ended': {
      const best = message.standings[0];
      return notice(`${best.name} en maat winnen met ${best.total}`);
    }

    // Already in the snapshot that follows, or belongs to another game.
    case 'card-played':
    case 'round-dealt':
    case 'bid-placed':
    case 'all-bids-in':
    case 'round-scored':
    case 'hand-dealt':
    case 'cards-exchanged':
    case 'hand-scored':
      return {};

    default: {
      const exhaustive: never = message;
      console.warn('unhandled server message', exhaustive);
      return {};
    }
  }
}

export const useKlaverjasLive = create<KlaverjasLiveData & KlaverjasLiveActions>()((set) => ({
  ...EMPTY,
  handle: (message) => set((prev) => reduce(prev, message)),
  setStatus: (status) => set({ status }),
  dismissError: () => set({ error: null, errorCode: null }),
  reset: () => set({ ...EMPTY }),
}));

export const myKlaverjasPlayer = (data: KlaverjasLiveData) =>
  data.seat === null ? null : (data.state?.players[data.seat] ?? null);

export const myKlaverjasHand = (data: KlaverjasLiveData): Card[] =>
  myKlaverjasPlayer(data)?.hand ?? [];

export const isMyKlaverjasTurn = (data: KlaverjasLiveData) =>
  data.seat !== null && data.state?.turnSeat === data.seat;

export const isMyChoice = (data: KlaverjasLiveData) =>
  data.seat !== null && data.state?.chooserSeat === data.seat;
